import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getUserRole } from '../lib/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'cliente';
}

export function ProtectedRoute({ children, requiredRole = 'cliente' }: ProtectedRouteProps) {
  const [hasAccess, setHasAccess] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const navigate = useNavigate();
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;

    const checkAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          if (mounted.current) {
            navigate('/');
          }
          return;
        }

        const userRole = await getUserRole();
        
        const hasRequiredAccess = requiredRole === 'admin' 
          ? userRole === 'admin'
          : true;

        if (!hasRequiredAccess) {
          if (mounted.current) {
            navigate('/unauthorized');
          }
          return;
        }

        if (mounted.current) {
          setHasAccess(true);
        }
      } catch (err) {
        console.error('Error checking access:', err);
        if (mounted.current) {
          navigate('/');
        }
      } finally {
        if (mounted.current) {
          setIsLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      mounted.current = false;
    };
  }, [navigate, requiredRole]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return <>{children}</>;
}